// Updated AppNavbar.jsx - Fixed zoom issues
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Navbar, Button, Container, Dropdown } from 'react-bootstrap';
import { ArrowLeft, Globe, MapPin, ChevronDown, Grid } from 'lucide-react';
import { useLanguage , i18n} from '../context/LanguageContext';
import { useExhibition } from '../context/ExhibitionContext';
import { useFavorites } from '../utils/favoritesService';
import NotificationManager from './Notifications';

const AppNavbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { language, toggleLanguage } = useLanguage();
  const { selectedExhibition, setSelectedExhibition, exhibitions } = useExhibition();
  const { favorites } = useFavorites(selectedExhibition?.value);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const isHomePage = location.pathname === '/';

  // Pages scroll in inner containers, not on window — capture-phase
  // listener catches scroll events from any of them.
  useEffect(() => {
    const onScroll = (e) => {
      if (e.target instanceof Element) {
        setIsScrolled(e.target.scrollTop > 8);
      }
    };
    document.addEventListener('scroll', onScroll, true);
    return () => document.removeEventListener('scroll', onScroll, true);
  }, []);

  // New page starts unscrolled
  useEffect(() => {
    setIsScrolled(false);
  }, [location.pathname]);
  
  const handleGoBack = () => {
    navigate(-1);
  };

  const handleExhibitionSelect = (exhibition) => {
    setSelectedExhibition(exhibition);
    navigate('/');
  };
  
  const handleClearSelection = () => {
    setSelectedExhibition(null);
    navigate('/');
  };
  
  const getExhibitionLabel = (exhibition) => {
    if (!exhibition) return 'HELEXPO';
    
    // Use labelEng for English, label for Greek (default)
    if (language === 'en' && exhibition.labelEng) {
      return exhibition.labelEng;
    }
    
    return exhibition.label;
  };
  
  const navbarBrandText = selectedExhibition ? getExhibitionLabel(selectedExhibition) : 'HELEXPO';
  
  return (
    <Navbar
      variant="dark"
      className={`app-navbar${isScrolled ? ' is-scrolled' : ''}`}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1030,
        backgroundColor: 'var(--app-glass-bg)',
        backdropFilter: 'var(--app-glass-blur)',
        WebkitBackdropFilter: 'var(--app-glass-blur)',
        height: '64px',
        minHeight: '64px',
        maxHeight: '64px',
        padding: '0',
        WebkitTextSizeAdjust: '100%',
        textSizeAdjust: '100%',
      }}
    >
      <Container fluid className="d-flex justify-content-between align-items-center h-100 px-3">
        <div className="d-flex align-items-center">
          {!isHomePage && (
            <Button
              onClick={handleGoBack}
              className="me-2 rounded-circle p-0 d-flex align-items-center justify-content-center app-navbar-icon-btn"
              style={{
                width: '38px',
                height: '38px',
                background: 'var(--app-surface-2)',
                border: '1px solid var(--app-surface-border)',
                fontSize: '16px',
              }}
            >
              <ArrowLeft size={18} color="#f8fafc" />
            </Button>
          )}

          <Dropdown show={showDropdown} onToggle={(isOpen) => setShowDropdown(isOpen)}>
            <Dropdown.Toggle
              as="div"
              className="d-flex align-items-center cursor-pointer app-navbar-brand-toggle"
              style={{
                cursor: 'pointer',
                padding: '6px 10px 6px 8px',
                borderRadius: 'var(--app-radius-pill)',
                background: 'var(--app-surface-1)',
                border: '1px solid var(--app-surface-border)',
                transition: 'background var(--app-transition)',
                WebkitTextSizeAdjust: '100%',
                textSizeAdjust: '100%',
              }}
            >
              <MapPin size={18} className="me-1" color="var(--app-accent)" />
              <Navbar.Brand
                className="mb-0 d-flex align-items-center"
                style={{
                  color: '#f8fafc',
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  letterSpacing: '-0.01em',
                  margin: 0,
                  WebkitTextSizeAdjust: '100%',
                  textSizeAdjust: '100%',
                }}
              >
                 {navbarBrandText.length > 15
                ? navbarBrandText.slice(0, 15) + '...'
                : navbarBrandText}
                <ChevronDown size={16} className="ms-1" color="rgba(248, 250, 252, 0.6)" />
              </Navbar.Brand>
            </Dropdown.Toggle>

            <Dropdown.Menu className="border-0 mt-2 app-navbar-dropdown">
              
              <Dropdown.Item 
                onClick={handleClearSelection}
                className="d-flex align-items-center py-2 border-bottom"
                style={{
                  fontSize: '16px', // ✅ Added - prevents iOS zoom
                  WebkitTextSizeAdjust: '100%', // ✅ Added
                  textSizeAdjust: '100%' // ✅ Added
                }}
              >
                <div 
                  className="me-2 rounded overflow-hidden bg-primary d-flex align-items-center justify-content-center" 
                  style={{ width: '30px', height: '30px', flexShrink: 0 }}
                >
                  <Grid size={16} color="white" />
                </div>
                <span className="fw-medium">{i18n.t('exhibitionView')}</span>
              </Dropdown.Item>
              
              {exhibitions && exhibitions.map((exhibition) => (
                <Dropdown.Item 
                  key={exhibition.label}
                  active={selectedExhibition?.label === exhibition.label}
                  onClick={() => handleExhibitionSelect(exhibition)}
                  className="d-flex align-items-center py-2"
                  style={{
                    fontSize: '16px', // ✅ Added - prevents iOS zoom
                    WebkitTextSizeAdjust: '100%', // ✅ Added
                    textSizeAdjust: '100%' // ✅ Added
                  }}
                >
                  <div 
                    className="me-2 rounded overflow-hidden" 
                    style={{ width: '30px', height: '30px', flexShrink: 0 }}
                  >
                    {exhibition.bannerUrl ? (
                      <img 
                        src={exhibition.bannerUrl} 
                        alt="" 
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <div 
                        className="d-flex align-items-center justify-content-center h-100"
                        style={{ backgroundColor: '#025483', color: 'white' }}
                      >
                        {getExhibitionLabel(exhibition).charAt(0)}
                      </div>
                    )}
                  </div>
                  <span>{getExhibitionLabel(exhibition)}</span>
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>
        
        <div className="d-flex align-items-center" style={{ gap: '8px' }}>
        {selectedExhibition && (
          <button
            type="button"
            className="navbar-fav-btn"
            onClick={() => navigate('/favorites')}
            aria-label="My Visit"
          >
            <i
              className={`bi ${favorites.length ? 'bi-heart-fill' : 'bi-heart'}`}
              style={{ color: favorites.length ? '#f472b6' : undefined, fontSize: '1rem' }}
            />
            {favorites.length > 0 && (
              <span className="navbar-fav-badge">{favorites.length}</span>
            )}
          </button>
        )}

        <Button
          onClick={toggleLanguage}
          className="d-flex align-items-center justify-content-center rounded-pill px-3 py-2 app-navbar-lang-btn"
          style={{
            background: 'var(--app-surface-2)',
            border: '1px solid var(--app-surface-border)',
            color: '#f8fafc',
            fontSize: '0.9rem',
            WebkitTextSizeAdjust: '100%',
            textSizeAdjust: '100%',
          }}
        >
          <Globe size={16} className="me-2" color="var(--app-accent)" />
          <span style={{
            fontSize: '0.9rem',
            fontWeight: 500,
            color: '#f8fafc',
            WebkitTextSizeAdjust: '100%',
            textSizeAdjust: '100%'
          }}>
            {language === 'gr' ? '🇬🇷 GR' : '🇬🇧 EN'}
          </span>
        </Button>
        </div>

      </Container>
    </Navbar>
  );
};

export default AppNavbar;